/**
 * Field Mappings
 *
 * Defines how shape properties map to data source fields.
 * Uses an EXCLUSION-based approach: all properties sync by default
 * except for structural/positional properties.
 */

/**
 * Mapping between a shape property and a data field
 */
export interface FieldMapping {
  /** Shape property name (e.g., "text", "fillColor") */
  shapeProperty: string;
  /** Data source field name (e.g., "Name", "Status") */
  dataField: string;
  /** Whether this mapping is bidirectional (shape changes update data) */
  bidirectional: boolean;
}

/**
 * Properties that should NOT be synced across bound shapes.
 * Everything else will be synced automatically.
 */
export const EXCLUDED_FROM_SYNC = new Set([
  // Structural properties
  'id',
  'type',
  'category',
  'zIndex',

  // Position (each shape has its own position)
  'x',
  'y',

  // Dimensions (each shape can have its own size)
  'width',
  'height',
  'w',
  'h',

  // Internal structures (handled separately)
  'props',
  'attrs',
  'style',
  'dataBinding',
  // Org chart custom field values are diagram presentation overrides. They
  // persist in Yjs but are never written back to the bound source.
  'customFieldOverrides',

  // State flags
  'locked',
  'visible',
  'selected',
  'rotation',
]);

/**
 * Default field mappings for common shape properties
 * Maps shape property names to data field names for the central store
 *
 * Note: Properties NOT in this list will use their property name as-is
 * for the data field (e.g., 'duration' -> 'duration'). This list is for
 * cases where we want different naming (e.g., 'text' -> 'Name').
 */
export const DEFAULT_FIELD_MAPPINGS: FieldMapping[] = [
  // Text/Name mappings - multiple shape properties map to same data field
  { shapeProperty: 'text', dataField: 'Name', bidirectional: true },
  { shapeProperty: 'name', dataField: 'Name', bidirectional: true },
  { shapeProperty: 'label', dataField: 'Name', bidirectional: true },

  // Process metadata mappings (PascalCase for data fields)
  { shapeProperty: 'description', dataField: 'Description', bidirectional: true },
  { shapeProperty: 'owner', dataField: 'Owner', bidirectional: true },
  { shapeProperty: 'status', dataField: 'Status', bidirectional: true },
  { shapeProperty: 'duration', dataField: 'Duration', bidirectional: true },

  // Extended properties
  { shapeProperty: 'sla', dataField: 'SLA', bidirectional: true },
  { shapeProperty: 'systems', dataField: 'Systems', bidirectional: true },
  { shapeProperty: 'tags', dataField: 'Tags', bidirectional: true },
  { shapeProperty: 'linkedDiagramId', dataField: 'LinkedDiagramId', bidirectional: true },

  // Start/End specific
  { shapeProperty: 'triggerType', dataField: 'TriggerType', bidirectional: true },
  { shapeProperty: 'outcomeType', dataField: 'OutcomeType', bidirectional: true },
];

/**
 * Shape properties that are considered "data-related" and should be synced
 * across bound shapes. This is now a computed check - anything NOT in
 * EXCLUDED_FROM_SYNC is data-related. This set is kept for reference/documentation.
 */
export const DATA_RELATED_SHAPE_PROPERTIES = new Set([
  // Core display
  'text',
  'name',
  'label',

  // Process metadata (Tier 1 & 2)
  'description',
  'owner',
  'status',
  'duration',

  // Extended properties (Tier 3)
  'sla',
  'systems',
  'tags',
  'linkedDiagramId',

  // Start/End specific
  'triggerType',
  'outcomeType',

  // Visual styling
  'fillColor',
  'strokeColor',
  'textColor',
  'fontSize',
  'fontFamily',
  'fontWeight',
]);

/**
 * Shape properties that are nested in attrs
 * Note: 'text' is intentionally excluded because flowchart shapes
 * check node.text at top level first, then fall back to attrs.label.
 * We need 'text' to be applied at top level for sibling sync to work.
 */
export const ATTRS_PROPERTIES = new Set([
  'label',
  'variant',
  'category',
]);

/**
 * Get the data field name for a shape property
 */
export function getDataFieldForProperty(
  shapeProperty: string,
  customMappings?: FieldMapping[]
): string | null {
  const mappings = customMappings || DEFAULT_FIELD_MAPPINGS;
  const mapping = mappings.find(m => m.shapeProperty === shapeProperty);
  return mapping?.dataField ?? null;
}

/**
 * Get the shape property for a data field
 */
export function getShapePropertyForField(
  dataField: string,
  customMappings?: FieldMapping[]
): string | null {
  const mappings = customMappings || DEFAULT_FIELD_MAPPINGS;
  const mapping = mappings.find(m => m.dataField === dataField);
  return mapping?.shapeProperty ?? null;
}

/**
 * Check if a shape property should be synced to data.
 * Uses exclusion-based approach: returns true for anything NOT excluded.
 */
export function isDataRelatedProperty(property: string): boolean {
  return !EXCLUDED_FROM_SYNC.has(property);
}

/**
 * Extract data-related changes from a shape update.
 * Uses EXCLUSION-based approach: extracts ALL properties except those
 * in EXCLUDED_FROM_SYNC. This allows custom properties to sync automatically.
 *
 * For known mappings (text/name/label -> Name), uses the mapped data field name.
 * For unknown properties, uses the property name as-is (e.g., description -> description).
 */
export function extractDataChanges(
  shapeChanges: Record<string, unknown>,
  customMappings?: FieldMapping[],
  cachedRecord?: Record<string, unknown>
): Record<string, unknown> {
  const dataChanges: Record<string, unknown> = {};
  const mappings = customMappings || DEFAULT_FIELD_MAPPINGS;

  // Helper to get data field name for a property
  const getDataFieldName = (property: string): string => {
    const mapping = mappings.find(m => m.shapeProperty === property && m.bidirectional);
    if (!mapping) return property;
    if (cachedRecord) {
      const hasMappedField = Object.prototype.hasOwnProperty.call(cachedRecord, mapping.dataField);
      const hasPropertyField = Object.prototype.hasOwnProperty.call(cachedRecord, property);
      if (!hasMappedField && hasPropertyField) {
        return property;
      }
    }
    return mapping.dataField;
  };

  // Check top-level properties - sync everything not excluded
  for (const [property, value] of Object.entries(shapeChanges)) {
    if (EXCLUDED_FROM_SYNC.has(property)) {
      continue;
    }

    const dataFieldName = getDataFieldName(property);
    dataChanges[dataFieldName] = value;
  }

  // Check attrs - sync everything not excluded
  const attrs = shapeChanges.attrs as Record<string, unknown> | undefined;
  if (attrs) {
    for (const [property, value] of Object.entries(attrs)) {
      if (EXCLUDED_FROM_SYNC.has(property)) continue;

      const dataFieldName = getDataFieldName(property);
      dataChanges[dataFieldName] = value;
    }
  }

  // Check props - sync everything not excluded (except internal structures)
  const props = shapeChanges.props as Record<string, unknown> | undefined;
  if (props) {
    for (const [property, value] of Object.entries(props)) {
      if (EXCLUDED_FROM_SYNC.has(property)) continue;
      if (property === 'dataBinding' || property === 'attrs') continue;

      const dataFieldName = getDataFieldName(property);
      dataChanges[dataFieldName] = value;
    }
  }

  return dataChanges;
}

/**
 * Apply data field values to a shape (for syncing from data to shape)
 */
export function applyDataToShape(
  dataRecord: Record<string, unknown>,
  customMappings?: FieldMapping[]
): { topLevel: Record<string, unknown>; attrs: Record<string, unknown> } {
  const topLevel: Record<string, unknown> = {};
  const attrs: Record<string, unknown> = {};
  const mappings = customMappings || DEFAULT_FIELD_MAPPINGS;

  for (const mapping of mappings) {
    const value = dataRecord[mapping.dataField];
    if (value === undefined) continue;

    if (ATTRS_PROPERTIES.has(mapping.shapeProperty)) {
      attrs[mapping.shapeProperty] = value;
    } else {
      topLevel[mapping.shapeProperty] = value;
    }
  }

  return { topLevel, attrs };
}

/**
 * Create a shape update object from data changes
 * Used when syncing data to shapes (both source and siblings)
 *
 * IMPORTANT: For text-related fields (text, label, name), we put the value
 * in BOTH topLevel AND attrs because different shape types store text
 * in different places:
 * - Flowchart shapes: check node.text first, fall back to attrs.label
 * - Some shapes: use attrs.text
 * - Some shapes: use top-level name
 *
 * For custom/unknown properties, applies them at top level.
 */
export function createShapeUpdateFromData(
  dataChanges: Record<string, unknown>,
  customMappings?: FieldMapping[]
): { topLevel: Record<string, unknown>; attrs: Record<string, unknown> } {
  const topLevel: Record<string, unknown> = {};
  const attrs: Record<string, unknown> = {};
  const mappings = customMappings || DEFAULT_FIELD_MAPPINGS;

  for (const [dataField, value] of Object.entries(dataChanges)) {
    // Find all shape properties mapped to this data field
    const relatedMappings = mappings.filter(m => m.dataField === dataField);

    if (relatedMappings.length > 0) {
      // Known mapping - apply to mapped shape properties
      for (const mapping of relatedMappings) {
        // For text-related properties, put in BOTH locations
        // to ensure all shape types receive the update
        if (mapping.shapeProperty === 'text' || mapping.shapeProperty === 'label') {
          topLevel[mapping.shapeProperty] = value;
          attrs[mapping.shapeProperty] = value;
        } else if (ATTRS_PROPERTIES.has(mapping.shapeProperty)) {
          attrs[mapping.shapeProperty] = value;
        } else {
          topLevel[mapping.shapeProperty] = value;
        }
      }
    } else {
      // No mapping found - apply property directly at top level
      // This handles custom properties like 'description', 'status', etc.
      topLevel[dataField] = value;
      // Also put in attrs for shapes that read from there
      attrs[dataField] = value;
    }
  }

  return { topLevel, attrs };
}
